import axios from 'axios'
import $date from '@services/date/index.js'
import { isSuccess } from '@/helpers/http-status-helpers'
import { isCacheFresh } from '@/helpers/cache-helpers'
import { success, fail } from '@/helpers/result-helper'
import toast from '@services/toasts/index.js'

const getDefaultState = () => {
  return {
    cached: {
      list: [],
      lastUpdated: null,
    },
    loadingCount: 0,
    crudLoadingCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,

  getters: {
    user: (state) => (id) => {
      return state.cached.list.find((user) => user.id === id)
    },
    users: (state) => {
      return state.cached
    },
    isLoadingUsers: (state) => state.loadingCount > 0,
    isLoadingUserCRUD: (state) => state.crudLoadingCount > 0,
  },

  mutations: {
    CACHE_USERS(state, users) {
      state.cached.list = users
      state.cached.lastUpdated = users ? $date() : null
    },
    CACHE_USER(state, newUser) {
      state.cached.list.push(newUser)
    },
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },

  actions: {
    fetchUser({ commit, state, rootState }, { username }) {
      // 1. Check if we already have the user as a current user.
      const { currentUser } = rootState.auth
      if (currentUser && currentUser.username === username) {
        return Promise.resolve(currentUser)
      }

      // 2. Check if we've already fetched and cached the user.
      const matchedUser = state.cached.list.find(
        (user) => user.username === username
      )
      if (matchedUser) {
        return Promise.resolve(matchedUser)
      }

      // 3. Fetch the user from the API and cache it in case
      //    we need it again in the future.
      return axios.get(`/api/users/${username}`).then((response) => {
        const user = response.data
        commit('CACHE_USER', user)
        return user
      })
    },
    async fetchAllStaff({ commit, getters }, forceRefresh = false) {
      // Check if users have already been cached
      if (
        getters.users.list &&
        getters.users.list.length > 0 &&
        isCacheFresh({
          cacheDuration: 3,
          duractionUnits: 'hours',
          lastUpdated: getters.users.lastUpdated,
          forceRefresh,
        })
      )
        return Promise.resolve(success(getters.users.list))

      commit('START_LOADING')

      // Fetch all admin members and cache them
      try {
        const response = await this.$api.staff.get()

        if (isSuccess(response.status)) {
          commit('CACHE_USERS', response.data)

          return success()
        }
      } catch (ex) {
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING')
      }
    },
    async fetchStaffById({ commit, getters, dispatch }, id) {
      commit('START_LOADING')

      try {
        // 1. Ensure admin list is populated
        await dispatch('fetchAllStaff')

        // 2. Check if we've already fetched and cached the user.
        const found = getters.user(id)
        if (found) return success(found)

        // 3. Fetch the user from the API and cache it in case
        //    we need it again in the future.
        const response = await this.$api.staff.get()

        if (isSuccess(response.status)) {
          commit('CACHE_USER', response.data)

          return success(response.data)
        }
      } catch (ex) {
        toast.show('Oops.. something went wrong')
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING')
      }
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
